import {
  ChakraFlex,
  Drawer,
  FireflyButton,
  FAIconName,
  FireflyTertiaryButtonMenu,
  InquiryDetailSectionHeader,
  EmptySectionCard,
  Text,
  ChakraBox,
  SkeletonLoader,
  useToastMessage,
} from '@equip.health/ui';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useGetProviderTimezonesEnumQuery } from '~/store/services/enumerations';
import { SYSTEM_TIME_ZONE } from '~/lib/util/calendar.util';
import { isPastDateTime } from '~/lib/util/classes/groupClasses.util';

import { useGetGroupClassDetailsV2Query } from '~/store/services/groupClass';

import CreateEditGroupModal from '../modals/CreateEditGroup';
import GroupDetailsCard from './GroupDetailsCard';
import CancelSectionModal from '../modals/CancelSectionModal';
import GroupClassDetailsDrawerHeader from './GroupClassDetailsDrawerHeader';
import CreateEditSectionModal from '../modals/CreateEditSection/CreateEditSectionModal';
import GroupSectionsList from '../common/GroupSections';
import { getTimezoneAbbreviation } from '~/lib/util/timezone.util';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { MaudPermissions } from '~/lib/constants/permissions.constants';

export interface GroupClassDetailsDrawerV2Props {
  groupClassId: string;
  isOpen: boolean;
  onClose: () => void;
  handleSuccess?: () => void;
}

type SectionEditEligibility = Partial<
  Pick<SectionData, 'startDate' | 'endDate' | 'frequency'>
>;

const TOP_LEVEL_ID = 'group-details-v2';

type ModalState =
  | { type: 'closed' }
  | { type: 'editGroup' }
  | { type: 'createSection' }
  | { type: 'editSection'; sectionId: string }
  | { type: 'cancelSection'; sectionId: string };

const getModalDerivedState = (modalState: ModalState) => {
  const isCreateGroupModalOpen = modalState.type === 'editGroup';
  const isCreateEditSectionModalOpen =
    modalState.type === 'createSection' || modalState.type === 'editSection';
  const isCancelSectionModalOpen = modalState.type === 'cancelSection';

  let selectedGroupSectionId: string | undefined;

  if (
    modalState.type === 'editSection' ||
    modalState.type === 'cancelSection'
  ) {
    selectedGroupSectionId = modalState.sectionId;
  }

  return {
    isCreateGroupModalOpen,
    isCreateEditSectionModalOpen,
    isCancelSectionModalOpen,
    selectedGroupSectionId,
  };
};

const canEditSection = (
  groupStatusCode: string | undefined,
  { startDate, endDate, frequency }: SectionEditEligibility,
): boolean => {
  if (groupStatusCode !== 'LIVE') {
    return true;
  }
  if (frequency !== 'DOES_NOT_REPEAT') {
    return !endDate || !isPastDateTime(endDate);
  }
  return !isPastDateTime(startDate);
};

const GroupClassDetailsDrawer = ({
  groupClassId,
  isOpen,
  onClose,
  handleSuccess,
}: GroupClassDetailsDrawerV2Props) => {
  const { trackEvent } = useAnalytics();

  const hasDrawerChangesRef = useRef(false);
  const { permissions } = useGlobalContext();
  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];
  const [selectedTimezone, setSelectedTimezone] =
    useState<string>(SYSTEM_TIME_ZONE);

  const [modalState, setModalState] = useState<ModalState>({ type: 'closed' });

  const {
    isCreateGroupModalOpen,
    isCreateEditSectionModalOpen,
    isCancelSectionModalOpen,
    selectedGroupSectionId,
  } = getModalDerivedState(modalState);

  const { createToast } = useToastMessage();

  const { data: timezonesResponse } = useGetProviderTimezonesEnumQuery();

  const timeZones = useMemo(() => {
    return (
      timezonesResponse?.map(({ code, id }) => ({
        label: code,
        node: id,
      })) || []
    );
  }, [timezonesResponse]);

  const {
    data: groupDetails,
    isLoading,
    isError,
    refetch: refetchGroupDetails,
  } = useGetGroupClassDetailsV2Query(groupClassId, {
    skip: !groupClassId,
  });

  const isReadOnly =
    groupDetails?.groupStatusCode === 'ARCHIVED' || !isSchedulingAdmin;
  const isButtonDisabled = isLoading || isError;

  const showErrorToast = (errorText?: string) => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: `${TOP_LEVEL_ID}__error-toast`,
      text:
        errorText ??
        "We're sorry, we were unable to load group details. Please try again",
      variant: 'critical',
    });
  };

  useEffect(() => {
    if (isError && groupDetails?.id !== groupClassId) {
      showErrorToast();
    }
  }, [isError, groupDetails, groupClassId]);

  const handleEditGroupClick = () => {
    if (isError || !groupDetails) {
      showErrorToast();
      return;
    }

    if (!navigator.onLine) {
      showErrorToast(
        'You are currently offline. Please check your connection and try again',
      );
    }

    trackEvent(GroupSectionEvents.editGroupButtonClicked, {
      Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
      'Group title': groupDetails.title,
      'Group status': groupDetails.groupStatusCode,
    });

    setModalState({ type: 'editGroup' });
  };

  const getSectionAnalyticsProperties = (
    dayOfWeek: string,
    startDate: string,
  ) => ({
    Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
    'Group title': groupDetails?.title,
    'Section day': dayOfWeek,
    'Section time': startDate,
  });

  const getKebabOptions = ({
    id,
    startDate,
    endDate,
    frequency,
    dayOfWeek,
  }: SectionData) => {
    const shouldShowEditOption = canEditSection(groupDetails?.groupStatusCode, {
      startDate,
      endDate,
      frequency,
    });

    return [
      ...(shouldShowEditOption
        ? [
            {
              label: 'Edit section',
              leftIcon: FAIconName.penRegular,
              leftIconColor: 'primary',
              onClick: () => {
                setModalState({ type: 'editSection', sectionId: id });
                trackEvent(
                  GroupSectionEvents.editSectionKebabMenuButtonClicked,
                  getSectionAnalyticsProperties(dayOfWeek, startDate),
                );
              },
            },
          ]
        : []),
      {
        label: 'Cancel section',
        leftIcon: FAIconName.trashRegular,
        leftIconColor: 'primary',
        onClick: () => {
          setModalState({ type: 'cancelSection', sectionId: id });
          trackEvent(
            GroupSectionEvents.cancelSectionKebabMenuButtonClicked,
            getSectionAnalyticsProperties(dayOfWeek, startDate),
          );
        },
      },
    ];
  };

  const DRAWER_PADDING_AND_GAP = 115; // Top/bottom padding (24px) + gaps (48px)
  const SECTION_HEADER_HEIGHT = 43; // Height of the section header (43px)

  const handleDrawerDataChanged = () => {
    hasDrawerChangesRef.current = true;
    refetchGroupDetails();
  };

  const handleDrawerClose = () => {
    onClose();

    if (hasDrawerChangesRef.current) {
      handleSuccess?.();
      hasDrawerChangesRef.current = false;
    }
  };

  const handleCloseModal = () => setModalState({ type: 'closed' });

  const groupSections = useMemo(
    () => groupDetails?.sections ?? [],
    [groupDetails],
  );

  const detailContentHeight = `calc(100vh - ${DRAWER_PADDING_AND_GAP}px)`;

  const sectionsContainerHeight = `calc(${detailContentHeight} - ${SECTION_HEADER_HEIGHT}px)`;

  const renderDrawerHeader = () => (
    <SkeletonLoader
      id={`${TOP_LEVEL_ID}__header-skeleton`}
      isLoaded={!isLoading}
    >
      {groupDetails ? (
        <GroupClassDetailsDrawerHeader
          title={groupDetails.title}
          groupId={groupClassId}
          currentStatus={groupDetails.groupStatusCode}
          onStatusChanged={handleDrawerDataChanged}
          isReadOnlyView={isReadOnly}
        />
      ) : null}
    </SkeletonLoader>
  );

  const renderGroupDetailsEditButton = () => {
    if (isReadOnly) {
      return null;
    }

    return (
      <FireflyButton
        id={`${TOP_LEVEL_ID}__edit-button`}
        isDisabled={isButtonDisabled}
        leftIcon={FAIconName.penRegular}
        onClick={handleEditGroupClick}
        size="small"
        variant="secondary"
      >
        Edit
      </FireflyButton>
    );
  };

  const renderGroupDetails = () => {
    if (groupDetails) {
      return <GroupDetailsCard groupDetails={groupDetails} />;
    }

    return (
      <ChakraBox
        id={`${TOP_LEVEL_ID}__error-container`}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="16px"
      >
        <EmptySectionCard
          caption={'Failed to load group details'}
          id={`${TOP_LEVEL_ID}__empty-section-card-details`}
        />
        <FireflyButton
          id={`${TOP_LEVEL_ID}__retry-button`}
          onClick={refetchGroupDetails}
        >
          Retry
        </FireflyButton>
      </ChakraBox>
    );
  };

  const renderCreateGroupModal = () => {
    if (!isCreateGroupModalOpen) {
      return null;
    }

    return (
      <CreateEditGroupModal
        groupExternalId={groupClassId}
        onClose={handleCloseModal}
        isOpen={isCreateGroupModalOpen}
        handleSuccess={handleDrawerDataChanged}
      />
    );
  };

  const renderAddSectionButton = () => {
    if (isReadOnly) {
      return null;
    }

    return (
      <FireflyButton
        id={`${TOP_LEVEL_ID}__add-section-button`}
        isDisabled={isButtonDisabled}
        leftIcon={FAIconName.plusSolid}
        onClick={() => {
          setModalState({ type: 'createSection' });
          trackEvent(GroupSectionEvents.addSectionButtonClicked, {
            Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
            'Group title': groupDetails?.title,
          });
        }}
        size="small"
        variant="secondary"
      >
        Add section
      </FireflyButton>
    );
  };

  const renderSectionModals = () => (
    <>
      {isCreateEditSectionModalOpen ? (
        <CreateEditSectionModal
          groupId={groupClassId}
          groupSectionId={selectedGroupSectionId}
          onClose={handleCloseModal}
          onSuccess={handleDrawerDataChanged}
          groupStatusCode={groupDetails.groupStatusCode}
          isOpen={isCreateEditSectionModalOpen}
        />
      ) : null}
      {isCancelSectionModalOpen && selectedGroupSectionId ? (
        <CancelSectionModal
          groupId={groupClassId}
          isOpen={isCancelSectionModalOpen}
          onClose={handleCloseModal}
          sectionExternalId={selectedGroupSectionId}
          onSuccess={handleDrawerDataChanged}
        />
      ) : null}
    </>
  );

  const kebabOptions = isReadOnly ? undefined : getKebabOptions;

  return (
    <Drawer
      id={TOP_LEVEL_ID}
      header={renderDrawerHeader()}
      isOpen={isOpen}
      onClose={handleDrawerClose}
    >
      <SkeletonLoader
        id={`${TOP_LEVEL_ID}__group-details-skeleton`}
        isLoaded={!isLoading}
      >
        <ChakraFlex paddingTop="24px" maxHeight="100%" overflow="hidden">
          <ChakraFlex
            paddingBottom="36px"
            justifyContent="space-between"
            gap="40px"
            width="100%"
            height={detailContentHeight}
          >
            {/* Group Details view */}
            <ChakraFlex
              alignItems="center"
              flexDirection="column"
              gap="12px"
              width="100%"
            >
              <ChakraFlex
                justifyContent="space-between"
                id={`${TOP_LEVEL_ID}__header`}
                width="100%"
              >
                <Text variant="h5">Group details</Text>
                {renderGroupDetailsEditButton()}
              </ChakraFlex>
              <ChakraBox width="100%">{renderGroupDetails()}</ChakraBox>
              {renderCreateGroupModal()}
            </ChakraFlex>
            {/* Sections */}
            <ChakraFlex direction="column" gap="12px" minWidth="350px">
              <InquiryDetailSectionHeader
                count={groupSections.length}
                id={`${TOP_LEVEL_ID}__sections-header`}
                title="Sections"
              >
                <FireflyTertiaryButtonMenu
                  buttonId={`${TOP_LEVEL_ID}__inquiry-view-select`}
                  options={timeZones}
                  maxHeight="300px"
                  buttonText={getTimezoneAbbreviation(selectedTimezone)}
                  onOptionClick={(option) => setSelectedTimezone(option)}
                />
                {renderAddSectionButton()}
              </InquiryDetailSectionHeader>
              <GroupSectionsList
                id={`${TOP_LEVEL_ID}__group-sections`}
                groupSections={groupSections}
                selectedTimezone={selectedTimezone}
                getKebabOptions={kebabOptions}
                height={sectionsContainerHeight}
              />
              {renderSectionModals()}
            </ChakraFlex>
          </ChakraFlex>
        </ChakraFlex>
      </SkeletonLoader>
    </Drawer>
  );
};

export default GroupClassDetailsDrawer;
